import { isFuseHolderVariant } from "./auto-fuse";
import type { DefLookup } from "./catalog";
import { effectiveTerminals } from "./effective";
import type { Cable, ComponentNode, FuseType, System, SystemNode } from "./types";

export type ProtectionDevice = {
  id: string;
  kind: "holder" | "battery-post" | "slot" | "cable";
  nodeIds: string[];
  cableIds: string[];
  /** Where this declared device touches its supply rail. */
  terminals: string[];
  family?: FuseType;
  ratingA?: number;
  /** Equal cable declarations might mirror this device or describe another
   * inline fuse. Equality cannot settle physical placement. */
  unresolvedCableDeclarations: string[];
  conflicts: { cableId: string; declaredA: number; deviceA?: number }[];
};

function attachedFuse(cable: Cable, nodes: readonly SystemNode[], lookup: DefLookup) {
  const attached: { node: ComponentNode; family?: FuseType; ratingA?: number }[] = [];
  for (const [id, terminalId] of [[cable.fromNodeId, cable.fromTerminalId], [cable.toNodeId, cable.toTerminalId]]) {
    const node = nodes.find(n => n.id === id);
    if (node?.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const terminal = effectiveTerminals(def, node).find(t => t.id === terminalId);
    if (terminal?.polarity !== "positive") continue;
    if (def.category === "battery" && node.batteryFuse) attached.push({ node, family: node.batteryFuse.type, ratingA: node.batteryFuse.ratingA });
    if (isFuseHolderVariant(def)) attached.push({ node, family: def.fuseSlotType, ratingA: node.fuseAmp ?? undefined });
  }
  return attached;
}

/** A cable declaration differing from an attached device is not evidence that
 * the attached device's family belongs to that cable declaration. */
export function hasConflictingInheritedFuse(cable: Cable, nodes: readonly SystemNode[], lookup: DefLookup): boolean {
  return conflictingFuseRatings(cable.fuseAmpOverride, attachedFuse(cable, nodes, lookup).map(device => device.ratingA));
}

export function conflictingFuseRatings(own: number | null | undefined, attached: readonly (number | undefined)[]): boolean {
  return own != null && attached.some(rating => rating != null && rating !== own);
}

/** Normalizes physical fuse identities without rewriting legacy project data.
 * It deliberately does not claim a selected manufacturer's fuse product. */
export function resolveProtectionDevices(system: System, lookup: DefLookup): ProtectionDevice[] {
  const devices: ProtectionDevice[] = [];
  const claimed = new Set<string>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = lookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const holder = isFuseHolderVariant(def);
    const post = def.category === "battery" && node.batteryFuse;
    if (!holder && !post) continue;
    const terminals = effectiveTerminals(def, node).filter(t => t.polarity === "positive");
    const ids = new Set(terminals.map(t => t.id));
    const attached = system.cables.filter(c =>
      (c.fromNodeId === node.id && ids.has(c.fromTerminalId)) ||
      (c.toNodeId === node.id && ids.has(c.toTerminalId)),
    );
    const family = post ? node.batteryFuse?.type : def.fuseSlotType;
    const declarations = attached.filter(c => c.fuseAmpOverride != null && c.fuseAmpOverride > 0 &&
      (!c.fuseTypeOverride || c.fuseTypeOverride === family));
    const distinct = [...new Set(declarations.map(c => c.fuseAmpOverride!))];
    const explicit = post ? node.batteryFuse?.ratingA : node.fuseAmp;
    const ratingA = explicit != null && explicit > 0 ? explicit : distinct.length === 1 ? distinct[0] : undefined;
    if (ratingA == null && declarations.length === 0) continue;
    const device: ProtectionDevice = {
      id: `${holder ? "holder" : "post"}:${node.id}`,
      kind: holder ? "holder" : "battery-post", nodeIds: [node.id],
      cableIds: attached.map(c => c.id), terminals: terminals.map(t => `${node.id}:${t.id}`),
      family, ratingA, unresolvedCableDeclarations: [], conflicts: [],
    };
    for (const c of declarations) {
      claimed.add(c.id);
      if (ratingA == null || c.fuseAmpOverride !== ratingA) {
        device.conflicts.push({ cableId: c.id, declaredA: c.fuseAmpOverride!, deviceA: ratingA });
      } else device.unresolvedCableDeclarations.push(c.id);
    }
    devices.push(device);
  }
  for (const cable of system.cables) {
    if (claimed.has(cable.id) || cable.fuseAmpOverride == null || cable.fuseAmpOverride <= 0) continue;
    let slot: { nodeId: string; terminalId: string; family: FuseType } | undefined;
    for (const [nodeId, terminalId] of [[cable.fromNodeId, cable.fromTerminalId], [cable.toNodeId, cable.toTerminalId]]) {
      const node = system.nodes.find(n => n.id === nodeId);
      if (node?.kind !== "component") continue;
      const def = lookup(node.definitionId, node.customDefinition);
      if (!def?.isFuseBlock || isFuseHolderVariant(def) || !def.fuseSlotType) continue;
      const terminal = effectiveTerminals(def, node).find(t => t.id === terminalId);
      if (terminal?.polarity === "positive" && terminal.direction === "output") slot = { nodeId, terminalId, family: def.fuseSlotType };
    }
    // A family explicitly differing from a block describes another inline
    // declaration; never overwrite it with the block's slot family.
    if (slot && cable.fuseTypeOverride && cable.fuseTypeOverride !== slot.family) slot = undefined;
    const id = slot ? `slot:${slot.nodeId}:${slot.terminalId}` : `cable:${cable.id}`;
    const existing = devices.find(device => device.id === id);
    if (existing) {
      existing.cableIds.push(cable.id);
      if (existing.ratingA !== cable.fuseAmpOverride) existing.conflicts.push({ cableId: cable.id, declaredA: cable.fuseAmpOverride, deviceA: existing.ratingA });
      else existing.unresolvedCableDeclarations.push(cable.id);
      continue;
    }
    devices.push({ id, kind: slot ? "slot" : "cable", nodeIds: slot ? [slot.nodeId] : [],
      cableIds: [cable.id], terminals: [`${cable.fromNodeId}:${cable.fromTerminalId}`, `${cable.toNodeId}:${cable.toTerminalId}`],
      family: cable.fuseTypeOverride ?? slot?.family, ratingA: cable.fuseAmpOverride,
      unresolvedCableDeclarations: [], conflicts: [],
    });
  }
  return devices;
}
